import type { WelcomeVideoConfig } from '../../domain/entities/WelcomeVideo';
import type {
  IProductRepository,
  ProductFilters,
  ProductSearchSuggestionsResult,
} from '../../domain/repositories/IProductRepository';
import {
  ApiCategoriesResponse,
  ApiCollectionResponse,
  CollectionPage,
  ApiFiltersResponse,
  ApiHomepageCollections,
  ApiMarketplaceAnnouncementStripResponse,
  ApiMarketplaceHeroBannerResponse,
  ApiProgramRewardOfferResponse,
  ApiMarketplaceShowcaseCardResponse,
  ApiProductResponse,
  ApiBrandsResponse,
  PaginatedResponse,
  Product,
  ProductCreateParams,
  ProductPaymentOptionsResponse,
  ProductPaymentPlanResponse,
  ProductStatus,
  ProductUpdateParams,
} from '../../domain/entities/Product';
import type { AuthTokenService } from '../services/AuthTokenService';
import axios from 'axios';
import type { IQuickIntroRepository, QuickIntroMutation, QuickIntroState, QuickIntroAdmission, QuickIntroDeviceLimits, QuickIntroPromptState } from '../../domain/repositories/IQuickIntroRepository';

export class ApiProductRepository implements IProductRepository, IQuickIntroRepository {
  private readonly apiClient;

  constructor(
    private readonly authTokenService: AuthTokenService,
    private readonly baseUrl: string
  ) {
    this.baseUrl = baseUrl;
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = this.authTokenService.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle token refresh
    this.apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const newToken = await this.authTokenService.refreshToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return this.apiClient(originalRequest);
        }
        return Promise.reject(error);
      }
    );
  }

  async create(product: ProductCreateParams): Promise<Product> {
    const response = await this.apiClient.post(
      '/v1/products',
      this.mapToApiFormat(product)
    );
    return this.mapFromApiResponse(response.data) as Product;
  }

  async findById(id: string): Promise<Product | null> {
    try {
      const response = await this.apiClient.get(`/v1/products/${id}`);
      return this.mapFromApiResponse(response.data) as Product;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async findByIds(ids: string[]): Promise<Product[]> {
    const response = await this.apiClient.get('/v1/products', {
      params: { ids: ids.join(',') },
    });
    return response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async findAll(filters?: ProductFilters): Promise<Product[]> {
    const params = this.buildQueryParams(filters);
    const response = await this.apiClient.get('/v1/products', { params });
    return response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async findByCategory(
    categoryId: string,
    filters?: ProductFilters
  ): Promise<PaginatedResponse<Product>> {
    return this.findProductPage({ ...filters, categories: [categoryId] });
  }

  async findByBrand(slug: string, filters?: ProductFilters): Promise<PaginatedResponse<Product>> {
    return this.findProductPage({ ...filters, brandName: slug });
  }

  private async findProductPage(filters: ProductFilters): Promise<PaginatedResponse<Product>> {
    const params = this.buildQueryParams(filters);
    const response = await this.apiClient.get('/v1/products', { params });
    const mapProducts = response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );

    return {
      ...response.data,
      results: mapProducts,
    };
  }

  async findByShop(
    shopId: string,
    filters?: ProductFilters
  ): Promise<Product[]> {
    const params = this.buildQueryParams(filters);
    params.merchant_shop_id = shopId;
    const response = await this.apiClient.get('/v1/products', { params });
    return response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async findFeatured(limit: number = 10): Promise<Product[]> {
    const response = await this.apiClient.get('/v1/products', {
      params: { featured: true, limit },
    });
    return response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async search(query: string, filters?: ProductFilters): Promise<Product[]> {
    const params = this.buildQueryParams(filters);
    params.q = query;
    const response = await this.apiClient.get('/v1/products', { params });
    return response.data.results.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async searchSuggestions(
    query: string,
    filters?: ProductFilters
  ): Promise<ProductSearchSuggestionsResult> {
    const params = this.buildQueryParams(filters);
    params.q = query;

    const response = await this.apiClient.get('/v1/products/search/suggestions', {
      params,
    });

    const products = Array.isArray(response.data?.products)
      ? response.data.products.map((item: ApiProductResponse) =>
          this.mapFromApiResponse(item)
        )
      : [];

    return {
      products,
      suggestions: Array.isArray(response.data?.suggestions)
        ? response.data.suggestions
        : [],
      source: response.data?.source || 'postgres',
    };
  }

  async update(id: string, updates: ProductUpdateParams): Promise<Product> {
    const response = await this.apiClient.patch(
      `/v1/products/${id}`,
      this.mapToApiFormat(updates)
    );
    return this.mapFromApiResponse(response.data) as Product;
  }

  async delete(id: string): Promise<void> {
    await this.apiClient.delete(`/v1/products/${id}`);
  }

  async exists(id: string): Promise<boolean> {
    try {
      await this.apiClient.head(`/v1/products/${id}`);
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  // New methods for API-specific functionality
  async findCollections(): Promise<any> {
    const response = await this.apiClient.get(
      '/v1/products/collections/homepage/'
    );
    return response.data.sections;
  }

  async findFeaturedCategories(limit?: number): Promise<ApiCategoriesResponse> {
    const params = limit ? { limit } : {};
    const response = await this.apiClient.get(
      '/v1/products/categories/featured',
      { params }
    );
    return response.data;
  }

  async findNavigationCategories(): Promise<ApiCategoriesResponse> {
    const response = await this.apiClient.get(
      '/v1/products/categories/navigation'
    );
    return response.data;
  }

  async findRelated(productId: string, limit: number = 5): Promise<Product[]> {
    const response = await this.apiClient.get(
      `/v1/products/${productId}/related`,
      {
        params: { limit },
      }
    );
    return response.data.related_products.map((item: ApiProductResponse) =>
      this.mapFromApiResponse(item)
    );
  }

  async getPaymentOptions(
    productId: string
  ): Promise<ProductPaymentOptionsResponse> {
    const response = await this.apiClient.get(
      `/v1/products/${productId}/payment-options`
    );
    return response.data;
  }

  async getPaymentPlans(
    productId: string
  ): Promise<ProductPaymentPlanResponse> {
    const response = await this.apiClient.get(
      `/v1/products/${productId}/payment-plans`
    );
    return response.data;
  }

  async getProductFilters(): Promise<ApiFiltersResponse> {
    const response = await this.apiClient.get('/v1/products/filters');
    return response.data;
  }

  async getCategoryNavigation(): Promise<ApiCategoriesResponse> {
    const response = await this.apiClient.get(
      '/v1/products/categories/navigation'
    );
    return response.data;
  }

  async getCategoryHierarchy(
    includeProductCount: boolean = true
  ): Promise<ApiCategoriesResponse> {
    const params = includeProductCount ? { include_product_count: 'true' } : {};
    const response = await this.apiClient.get(
      '/v1/products/categories/hierarchy',
      { params }
    );
    return response.data;
  }

  async getFeaturedCategories(
    isFeatured: boolean = true
  ): Promise<ApiCategoriesResponse> {
    if (isFeatured) {
      const response = await this.apiClient.get('/v1/products/categories/featured');
      const categories = response.data?.categories ?? response.data?.results ?? [];

      return {
        count: categories.length,
        next: undefined,
        previous: undefined,
        results: categories,
      };
    }

    const response = await this.apiClient.get(
      '/v1/products/categories/storefront/'
    );
    return response.data;
  }

  async getCollection(slug: string, page: number, pageSize: number): Promise<CollectionPage> {
    const response: { data: ApiCollectionResponse } = await this.apiClient.get(
      `/v1/products/collections/${encodeURIComponent(slug)}/`,
      { params: { page, page_size: pageSize } }
    );
    return {
      ...response.data,
      results: response.data.results.map((product) => this.mapFromApiResponse(product)),
    };
  }

  async getHomepageCollections(limit: number = 6): Promise<ApiHomepageCollections> {
    const response = await this.apiClient.get(
      '/v1/products/collections/homepage/',
      { params: { limit } }
    );
    return response.data;
  }

  async getBrands(params?: { limit?: number; is_active?: boolean }): Promise<ApiBrandsResponse> {
    type BrandPage = Omit<ApiBrandsResponse, 'count'> & {
      count?: number;
      pagination?: Pick<ApiBrandsResponse, 'count' | 'next'>;
    };
    const results: ApiBrandsResponse['results'] = [];
    const seen = new Set<string>();
    let page = 1;
    let hasNext: boolean;

    // The API paginates even when a larger limit is requested. Load the roster
    // before homepage curation so later alphabetical pages are represented.
    do {
      const response: { data: BrandPage } = await this.apiClient.get('/v1/products/brands/', {
        params: { page, limit: params?.limit ?? 20, is_active: params?.is_active ?? true },
      });
      const previousSize = seen.size;
      for (const brand of response.data.results) {
        if (!seen.has(brand.id)) {
          seen.add(brand.id);
          results.push(brand);
        }
      }
      hasNext = Boolean(response.data.pagination?.next ?? response.data.next);
      if (hasNext && seen.size === previousSize) {
        throw new Error('Brand pagination did not advance');
      }
      page += 1;
    } while (hasNext);

    return { results, count: results.length, next: null, previous: null };
  }

  async getWelcomeVideo(): Promise<WelcomeVideoConfig> {
    const response = await this.apiClient.get('/v1/products/home/welcome-video/?invitation_protocol=2', { timeout: 5000 });
    return response.data;
  }

  async getQuickIntroState(): Promise<QuickIntroState> {
    const response = await this.apiClient.get('/v1/consumer/quick-intro/state/', { timeout: 5000 });
    return response.data;
  }

  async reserveQuickIntro(accountId: string, revision: number, device?: QuickIntroDeviceLimits, invitationProtocol?: 2): Promise<{ admission: QuickIntroAdmission | null; state: QuickIntroState }> {
    const response = await this.apiClient.post('/v1/consumer/quick-intro/admission/', {
      expected_account_id: accountId, playlist_revision: revision, ...device,
      ...(invitationProtocol === 2 ? { invitation_protocol: 2 } : {}),
    }, { timeout: 5000 });
    return response.data;
  }

  async updateQuickIntroPrompt(accountId: string, token: string, action: 'renew' | 'release'): Promise<QuickIntroPromptState> {
    const response = await this.apiClient.post('/v1/consumer/quick-intro/prompt/', {
      expected_account_id: accountId, token, action,
    }, { timeout: 5000 });
    return response.data;
  }

  async updateQuickIntro(action: QuickIntroMutation, body: Record<string, unknown>): Promise<QuickIntroState> {
    const response = await this.apiClient.post(`/v1/consumer/quick-intro/${action}/`, body, { timeout: 5000 });
    return response.data;
  }

  async getMarketplaceHeroBanners(): Promise<ApiMarketplaceHeroBannerResponse> {
    const response = await this.apiClient.get('/v1/products/home/hero-banners/');
    return response.data;
  }

  async getMarketplaceShowcaseCards(): Promise<ApiMarketplaceShowcaseCardResponse> {
    const response = await this.apiClient.get('/v1/products/home/showcase-cards/');
    return response.data;
  }

  async getMarketplaceAnnouncementStrips(
    scope?: 'global' | 'homepage' | 'merchandising'
  ): Promise<ApiMarketplaceAnnouncementStripResponse> {
    const response = await this.apiClient.get('/v1/products/home/announcement-strips/', {
      params: scope ? { scope } : undefined,
    });
    return response.data;
  }

  async getProgramRewardOffers(): Promise<ApiProgramRewardOfferResponse> {
    const response = await this.apiClient.get('/v1/programs/reward-offers/');
    const payload = response.data;
    if (Array.isArray(payload)) {
      return { results: payload };
    }
    return {
      results: Array.isArray(payload?.results) ? payload.results : [],
    };
  }

  async setFavoriteStatus(productId: string, shouldFavorite: boolean): Promise<boolean> {
    const response = await this.apiClient.request({
      url: `/v1/products/${productId}/favorite/`,
      method: shouldFavorite ? 'POST' : 'DELETE',
    });

    return response.data?.is_favorite === true;
  }

  private buildQueryParams(filters?: ProductFilters): any {
    if (!filters) return {};

    const params: any = {};

    if (filters.categories?.length) {
      params.category = filters.categories.join(',');
    }
    if (filters.includeDescendants) {
      params.include_descendants = 'true';
    }
    if (filters.brandName) {
      params.brand = filters.brandName;
    }
    if (filters.minPrice !== undefined) {
      params.price_min = filters.minPrice;
    }
    if (filters.maxPrice !== undefined) {
      params.price_max = filters.maxPrice;
    }
    if (filters.isFeatured !== undefined) {
      params.featured = filters.isFeatured;
    }
    if (filters.status) {
      params.status = filters.status;
    }
    if (filters.page) {
      params.page = filters.page;
    }
    if (filters.limit) {
      params.limit = filters.limit;
    }
    if (filters.offset) {
      params.offset = filters.offset;
    }
    if (filters.sortBy && filters.sortOrder) {
      params.sort = `${filters.sortBy}_${filters.sortOrder}`;
    }

    return params;
  }

  private mapFromApiResponse(apiData: ApiProductResponse): Product {
    // Map API field 'brand' to domain field 'brand_name' (list endpoint returns 'brand', detail returns 'brand_name')
    const mappedData = {
      ...apiData,
      brand_name: (apiData as any).brand_name ?? (apiData as any).brand,
    };
    return Product.fromObject(mappedData as any) as Product;
  }

  private mapToApiFormat(
    productData: Partial<ProductCreateParams | ProductUpdateParams>
  ): any {
    return {
      name: productData.name,
      brand_name: productData.brand_name,
      model_no: productData.model_no,
      description: productData.description,
      rich_description: productData.rich_description,
      cost_price: productData.cost_price,
      retail_price: productData.retail_price,
      max_payment_period: productData.max_payment_period,
      images: productData.images || [],
      categories: productData.categories,
      merchant_shop_id: productData.merchant_shop_id,
      shop_name: productData.shop_name,
      shop_logo: productData.shop_logo,
      lender_id: productData.lender_id,
      is_featured: productData.is_featured,
      status: productData.status,
    };
  }

  private mapApiStatusToDomain(apiStatus: string): ProductStatus {
    switch (apiStatus) {
      case 'in_stock':
      case 'A':
        return ProductStatus.ACTIVE;
      case 'out_of_stock':
      case 'O':
        return ProductStatus.OUT_OF_STOCK;
      case 'discontinued':
      case 'D':
        return ProductStatus.DISCONTINUED;
      default:
        return ProductStatus.INACTIVE;
    }
  }
}
